import { useRef } from 'react';
import { Phone, Droplets, Shield, Clock, Trash2, CheckCircle, Snowflake } from 'lucide-react';

// Real photos
const ACTION_PHOTO_1 = "https://customer-assets.emergentagent.com/job_blast-clean/artifacts/a69amg3f_IMG_0960.png";
const ACTION_PHOTO_2 = "https://customer-assets.emergentagent.com/job_blast-clean/artifacts/wuyle13v_IMG_0874.jpg";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_business-match-hub-1/artifacts/ly54u1mk_7A4F7B0F-B2FC-4B29-8962-F57D33FF73A6.png";

export const FlyerPage = () => {
  const flyerRef = useRef(null);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-zinc-900 py-8 px-4 print:p-0 print:bg-white">
      {/* Print Button - Hidden when printing */}
      <div className="max-w-[8.5in] mx-auto mb-4 print:hidden">
        <button
          onClick={handlePrint}
          className="bg-orange-600 text-black px-6 py-3 font-bold uppercase tracking-widest hover:bg-orange-500"
          style={{ fontFamily: 'Bebas Neue, sans-serif' }}
        >
          Print / Save as PDF
        </button>
        <p className="text-zinc-400 text-sm mt-2">Click to print or save as PDF for professional printing</p>
      </div>

      {/* Flyer - 8.5 x 11 inches */}
      <div 
        ref={flyerRef}
        className="max-w-[8.5in] mx-auto bg-[#0a1628] text-white shadow-2xl print:shadow-none"
        style={{ 
          minHeight: '11in',
          fontFamily: 'Arial, sans-serif'
        }}
      >
        {/* Header */}
        <div className="bg-[#0a1628] px-8 pt-8 pb-4 text-center relative overflow-hidden">
          {/* Snowflake decorations */}
          <div className="absolute top-4 left-4 opacity-30">
            <Snowflake className="w-8 h-8 text-cyan-400" />
          </div>
          <div className="absolute top-4 right-4 opacity-30">
            <Snowflake className="w-8 h-8 text-cyan-400" />
          </div>
          <div className="absolute top-12 left-16 opacity-20">
            <Snowflake className="w-6 h-6 text-cyan-400" />
          </div>
          <div className="absolute top-8 right-20 opacity-20">
            <Snowflake className="w-6 h-6 text-cyan-400" />
          </div>
          
          {/* Logo and Company Name */}
          <div className="flex items-center justify-center gap-4 mb-4">
            <img src={LOGO_URL} alt="23rd" className="h-16" />
            <div className="text-left">
              <h1 className="text-3xl font-bold tracking-wider text-white" style={{ fontFamily: 'Arial Black, sans-serif' }}>
                23RD
              </h1>
              <p className="text-sm tracking-widest text-cyan-400">ENVIRONMENTAL UNITS LLC</p>
            </div>
          </div>

          {/* Main Headline */}
          <h2 className="text-5xl font-black tracking-wide text-white mb-2" style={{ fontFamily: 'Arial Black, sans-serif' }}>
            DRY ICE BLASTING
          </h2>
          
          {/* Subheadline */}
          <p className="text-lg tracking-widest text-gray-300 mb-4">
            QUALITY. POWER. PERFORMANCE. CLEANER. SAFER. STRONGER.
          </p>

          {/* Eco Badge */}
          <div className="inline-block bg-[#7cb342] text-black px-6 py-2 rounded-full font-bold text-sm tracking-wide">
            ECO-FRIENDLY — SAFE FOR PEOPLE, EQUIPMENT & THE ENVIRONMENT
          </div>
        </div>

        {/* Photo Strip */}
        <div className="grid grid-cols-2 gap-1 px-1">
          <div className="h-40 overflow-hidden">
            <img src={ACTION_PHOTO_2} alt="Dry ice blasting engine" className="w-full h-full object-cover" />
          </div>
          <div className="h-40 overflow-hidden">
            <img src={ACTION_PHOTO_1} alt="Undercarriage cleaning" className="w-full h-full object-cover" />
          </div>
        </div>

        {/* Blue Banner - The Smarter Way */}
        <div className="bg-[#1565c0] py-6 px-8">
          <h3 className="text-2xl font-bold text-center mb-6 tracking-wide">
            THE SMARTER WAY TO PERFORM
          </h3>
          
          <div className="grid grid-cols-4 gap-4 text-center">
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center mb-2">
                <Droplets className="w-7 h-7 text-white" />
              </div>
              <p className="text-xs leading-tight">No Water or<br/>Harsh Chemicals</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center mb-2">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <p className="text-xs leading-tight">Safe for<br/>Sensitive Surfaces</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center mb-2">
                <Clock className="w-7 h-7 text-white" />
              </div>
              <p className="text-xs leading-tight">No Downtime<br/>Keep Moving</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center mb-2">
                <Trash2 className="w-7 h-7 text-white" />
              </div>
              <p className="text-xs leading-tight">No Secondary<br/>Waste Left Behind</p>
            </div>
          </div>
        </div>

        {/* Services Section */}
        <div className="bg-[#0d2137] py-8 px-8">
          <h3 className="text-2xl font-bold mb-6 tracking-wide">SERVICES</h3>
          
          <div className="grid grid-cols-2 gap-x-8 gap-y-3">
            {[
              'Fleet & Vehicle Cleaning',
              'Equipment & Machinery Cleaning',
              'Fire & Smoke Damage Restoration',
              'Mold Remediation',
              'Surface Preparation & Restoration',
              'Industrial Facility Cleaning',
              'HVAC System Cleaning',
              'Paint & Coating Removal'
            ].map((service, i) => (
              <div key={i} className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-[#7cb342] flex-shrink-0" />
                <span className="text-sm">{service}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-[#1565c0] py-4 px-8 text-center">
          <p className="text-xl font-bold tracking-wide">
            ORDER TODAY. PERFORM BETTER. GET A QUOTE NOW!
          </p>
        </div>

        {/* Footer / Contact */}
        <div className="bg-[#0a1628] py-6 px-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400 mb-1">Contact Us</p>
              <p className="font-bold">Lee Davis & Antwanette Pierce</p>
              <p className="text-cyan-400">23rd Environmental Units LLC</p>
            </div>
            
            <div className="text-center">
              <div className="flex items-center gap-2 justify-center mb-2">
                <Phone className="w-5 h-5 text-[#7cb342]" />
                <span className="text-2xl font-bold">817-616-0743</span>
              </div>
              <p className="text-xs text-gray-400">operations@23rdindustries.com</p>
            </div>

            <div className="text-right">
              {/* QR Code placeholder */}
              <div className="w-20 h-20 bg-white p-1 inline-block">
                <div className="w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyMSAyMSI+PHBhdGggZD0iTTEgMWg3djdIMVYxem0yIDJoM3YzSDNWM3ptOC0yaDd2N2gtN1Yxem0yIDJoM3YzaC0zVjN6TTEgMTNoN3Y3SDFWMTN6bTIgMmgzdjNoLTN2LTN6bTgtNmgxdjFoLTFWOXptMiAwaDN2MWgtM1Y5em00IDBoMXY0aC0xVjl6bS02IDJoMXYxaC0xdi0xem0yIDBoMXYyaC0ydi0xaDFWMTF6bTIgMGgxdjFoLTF2LTF6bTIgMHYxaC0xdi0xaDFabS02IDJoMXYxaC0xdi0xem0yIDBoMXYzaC0xdi0yaC0xdi0xaDFabTQgMGgydjFoLTF2MmgtMXYtMmgtMXYtMWgxem0tNCAyaDJ2MWgtMnYtMXptNSAwaDJ2MWgtMnYtMXptLTMgMmgxdjJoLTF2LTJ6bTIgMGgxdjFoLTF2LTF6bTIgMGgxdjJoLTF2LTJabS0yIDJoMXYxaC0xdi0xeiIgZmlsbD0iIzAwMCIvPjwvc3ZnPg==')] bg-contain"></div>
              </div>
              <p className="text-xs text-gray-400 mt-1">Scan for Quote</p>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-4 pt-4 text-center">
            <p className="text-sm tracking-widest text-gray-400">
              RESIDENTIAL • COMMERCIAL • INDUSTRIAL
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Serving DFW Metroplex & Surrounding Areas | Minority-Owned | Woman-Owned
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
